import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { resolveImage, resolvePrivateImage, formatRM, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Upload, MessageCircle, Check, X, Loader2 } from "lucide-react";

export default function AdminOrderDetail() {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const zh = !i18n.language?.startsWith("en");
  const [order, setOrder] = useState(null);
  const [settings, setSettings] = useState({});
  const [rejecting, setRejecting] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/orders/${id}`).then((r) => setOrder(r.data));

  useEffect(() => {
    load();
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (!order) return <div className="p-10 text-[#4a6e77]">{t("common.loading")}</div>;

  const confirm = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/orders/${order.id}/status`, { status: "confirmed" });
      toast.success(t("order.confirmed"));
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const reject = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/orders/${order.id}/status`, {
        status: "rejected",
        reject_reason: rejectReason,
      });
      toast.success(t("admin.rejectOrder"));
      setRejecting(false);
      setRejectReason("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/orders/${order.id}/proof`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(t("common.saved"));
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); e.target.value = ""; }
  };

  const waLink = () => {
    const num = (settings.whatsapp_number || order.shipping_phone || "").replace(/[^\d]/g, "");
    if (!num) return null;
    const msg = zh
      ? `您好 ${order.user_name}，您的订单 #${order.order_no} 金额 ${formatRM(order.total)} 已确认，感谢支持！`
      : `Hello ${order.user_name}, your order #${order.order_no} (${formatRM(order.total)}) is confirmed. Thank you!`;
    return `https://wa.me/${num}?text=${encodeURIComponent(msg)}`;
  };

  return (
    <div className="p-6 md:p-10" data-testid="admin-order-detail-page">
      <Link to="/admin/orders" className="text-sm text-[#4a6e77] hover:text-[#005870]">← {t("common.back")}</Link>
      <h1 className="mt-3 font-serif-cn text-3xl text-[#003645] font-semibold">
        {t("admin.orderDetail")} #{order.order_no}
      </h1>

      <div className="mt-6 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Items */}
          <div className="rounded-lg border border-[#e5ebeb] bg-white p-6">
            <div className="text-xs uppercase tracking-[0.3em] text-[#00868e] font-semibold">items</div>
            <ul className="mt-3 divide-y divide-[#e5ebeb]">
              {(order.items || []).map((it) => (
                <li key={it.product_id} className="py-3 flex items-center gap-4">
                  <div className="h-12 w-12 rounded overflow-hidden bg-[#f5f0e8] shrink-0">
                    {it.image && <img src={resolveImage(it.image)} alt="" className="h-full w-full object-cover" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[#003645] truncate">{zh ? it.name_zh : it.name_en}</div>
                    <div className="text-xs text-[#4a6e77]">× {it.quantity}</div>
                  </div>
                  <div className="text-[#003645] font-medium">{formatRM(it.subtotal)}</div>
                </li>
              ))}
              {(order.events || []).map((ev) => (
                <li key={"e-" + ev.event_id} className="py-3 flex items-center gap-4" data-testid={`admin-order-event-${ev.event_id}`}>
                  <div className="h-12 w-12 rounded overflow-hidden bg-[#f5f0e8] shrink-0 flex items-center justify-center">
                    {ev.image ? <img src={resolveImage(ev.image)} alt="" className="h-full w-full object-cover" /> : <span className="text-[10px] text-[#00868e]">EVENT</span>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-[#00868e] font-semibold">
                      {t("cartTypes.event")}
                    </div>
                    <div className="text-[#003645] truncate">{zh ? ev.title_zh : ev.title_en}</div>
                    <div className="text-xs text-[#4a6e77]">× {ev.quantity}{ev.starts_at ? ` · ${new Date(ev.starts_at).toLocaleString()}` : ""}</div>
                    {ev.name && <div className="text-xs text-[#4a6e77]">{ev.name}{ev.phone ? ` · ${ev.phone}` : ""}</div>}
                  </div>
                  <div className="text-[#003645] font-medium">{formatRM(ev.subtotal)}</div>
                </li>
              ))}
            </ul>
            <div className="mt-3 pt-3 border-t border-[#e5ebeb] space-y-1 text-sm">
              {(order.product_subtotal || 0) > 0 && (order.events_subtotal || 0) > 0 && (
                <>
                  <div className="flex justify-between text-[#4a6e77]" data-testid="admin-order-product-subtotal"><span>{t("cartTypes.product")}</span><span>{formatRM(order.product_subtotal)}</span></div>
                  <div className="flex justify-between text-[#4a6e77]" data-testid="admin-order-events-subtotal"><span>{t("cartTypes.event")}</span><span>{formatRM(order.events_subtotal)}</span></div>
                </>
              )}
              <div className="flex justify-between text-[#4a6e77]">
                <span>{t("common.subtotal")}</span>
                <span>{formatRM(order.total)}</span>
              </div>
              {(order.points_used || 0) > 0 && (
                <div className="flex justify-between text-[#00868e]">
                  <span>- {t("points.title")}</span>
                  <span>- {formatRM(order.points_used)}</span>
                </div>
              )}
              <div className="flex justify-between font-semibold pt-1">
                <span>{t("checkout.amount")}</span>
                <span className="text-[#005870]" data-testid="admin-order-cash-paid">{formatRM(order.cash_paid ?? order.total)}</span>
              </div>
            </div>
          </div>

          {/* Shipping */}
          <div className="rounded-lg border border-[#e5ebeb] bg-white p-6">
            <div className="text-xs uppercase tracking-[0.3em] text-[#00868e] font-semibold">customer & shipping</div>
            <div className="mt-3 text-sm text-[#003645] leading-relaxed">
              <div><b>{order.user_name}</b> · {order.user_email}</div>
              <div className="mt-1">{order.shipping_name} · {order.shipping_phone}</div>
              <div className="text-[#4a6e77] mt-1">{order.shipping_address}</div>
              {order.notes && <div className="text-[#4a6e77] mt-2">📝 {order.notes}</div>}
            </div>
          </div>

          {/* Proof (bank-in) OR Stripe paid summary */}
          {order.payment_method === "stripe" ? (
            <div className="rounded-lg border border-[#e5ebeb] bg-white p-6" data-testid="admin-stripe-section">
              <div className="text-xs uppercase tracking-[0.3em] text-[#00868e] font-semibold">payment · stripe</div>
              <div className="mt-3 text-sm text-[#003645]">
                <div><span className="text-[#4a6e77]">Status:</span> <span className={`font-medium ${order.stripe_payment_status === "paid" ? "text-[#00868e]" : "text-amber-700"}`}>{order.stripe_payment_status || "pending"}</span></div>
                {order.stripe_session_id && <div className="mt-1 text-xs text-[#4a6e77] break-all">session · {order.stripe_session_id}</div>}
                {order.stripe_payment_intent && <div className="text-xs text-[#4a6e77] break-all">intent · {order.stripe_payment_intent}</div>}
              </div>
              {order.status === "rejected" && (
                <div className="mt-3 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2" data-testid="admin-stripe-refund-notice">
                  {t("admin.stripeRefundNotice")}
                </div>
              )}
            </div>
          ) : (
          <div className="rounded-lg border border-[#e5ebeb] bg-white p-6" data-testid="admin-proof-section">
            <div className="text-xs uppercase tracking-[0.3em] text-[#00868e] font-semibold">payment proof</div>
            {order.payment_proof ? (
              <div className="mt-3">
                <a href={resolvePrivateImage(order.payment_proof)} target="_blank" rel="noopener noreferrer" data-testid="admin-view-proof">
                  <img src={resolvePrivateImage(order.payment_proof)} alt="proof" className="max-h-64 rounded border border-[#e5ebeb]" />
                </a>
              </div>
            ) : (
              <div className="mt-3 text-sm text-[#4a6e77]">尚未上传付款凭证 / No proof uploaded yet</div>
            )}
            <div className="mt-4">
              <label className="inline-flex items-center gap-2 text-sm text-[#005870] hover:text-[#00868e] cursor-pointer">
                <Upload size={14} /> {t("admin.uploadForCustomer")}
                <input type="file" accept="image/*" className="hidden" onChange={onUpload} disabled={busy} data-testid="admin-upload-proof-input" />
              </label>
            </div>
          </div>
          )}
        </div>

        {/* Action panel */}
        <div className="space-y-4">
          <div className="rounded-lg border border-[#e5ebeb] bg-white p-6">
            <div className="text-xs uppercase tracking-[0.3em] text-[#00868e] font-semibold">status</div>
            <div className="mt-2 text-sm font-medium text-[#003645]">
              {t(`order.statuses.${order.status}`)}
            </div>

            {order.status !== "confirmed" && (
              <button
                onClick={confirm}
                disabled={busy}
                className="mt-4 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-[#005870] text-white text-sm font-medium hover:bg-[#003645] transition-colors duration-200 disabled:opacity-60"
                data-testid="confirm-order-btn"
              >
                {busy ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                {t("admin.confirmOrder")}
              </button>
            )}

            {!rejecting && order.status !== "rejected" && (
              <button
                onClick={() => setRejecting(true)}
                className="mt-2 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full border border-red-300 text-red-700 text-sm font-medium hover:bg-red-50 transition-colors duration-200"
                data-testid="open-reject-btn"
              >
                <X size={14} /> {t("admin.rejectOrder")}
              </button>
            )}

            {rejecting && (
              <div className="mt-3" data-testid="reject-form">
                <textarea
                  rows={3}
                  placeholder={t("admin.rejectReasonHint")}
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  className="w-full px-3 py-2 rounded-md border border-[#e5ebeb] focus:border-[#00868e] outline-none text-sm"
                  data-testid="reject-reason-textarea"
                />
                <div className="mt-2 flex gap-2">
                  <button
                    onClick={reject}
                    disabled={busy || !rejectReason.trim()}
                    className="flex-1 px-3 py-2 rounded-full bg-red-600 text-white text-sm font-medium hover:bg-red-700 disabled:opacity-60"
                    data-testid="confirm-reject-btn"
                  >
                    {t("common.confirm")}
                  </button>
                  <button
                    onClick={() => { setRejecting(false); setRejectReason(""); }}
                    className="flex-1 px-3 py-2 rounded-full border border-[#e5ebeb] text-sm text-[#003645]"
                  >
                    {t("common.cancel")}
                  </button>
                </div>
              </div>
            )}

            {order.reject_reason && (
              <div className="mt-4 text-xs text-red-700 bg-red-50 rounded px-3 py-2">
                {order.reject_reason}
              </div>
            )}

            {waLink() && (
              <a
                href={waLink()}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 inline-flex items-center justify-center gap-2 w-full px-4 py-2.5 rounded-full bg-[#25D366] text-white text-sm font-medium hover:opacity-90 transition-opacity duration-200"
                data-testid="admin-wa-btn"
              >
                <MessageCircle size={14} /> {t("admin.contactCustomer")}
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
